const sameVariant = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

/**
 * Summarize filtered votes for a reference-vs-all survey.
 *
 * Votes are normalized by variant ID, never by the left/right position in the
 * rendered comparison. This keeps the result correct when pair orientation is
 * randomized by the backend.
 */
const summarizeReferenceResults = (
  votes = [],
  referenceVariantId,
  candidateVariants = []
) => {
  if (!Array.isArray(votes) || referenceVariantId === null || referenceVariantId === undefined) {
    return [];
  }

  const candidates = new Map();
  candidateVariants.forEach((variant) => {
    const candidateId = variant.variant_id ?? variant.variantId;
    if (candidateId === null || candidateId === undefined) return;
    if (!sameVariant(candidateId, referenceVariantId)) {
      candidates.set(String(candidateId), {
        candidateId,
        candidateName: variant.variant_name ?? variant.variantName ?? String(candidateId),
        referenceWins: 0,
        candidateWins: 0,
        ties: 0,
        equallyBad: 0,
      });
    }
  });

  votes.forEach((vote) => {
    const winnerIsReference = sameVariant(
      vote.variant_id_winner,
      referenceVariantId
    );
    const loserIsReference = sameVariant(
      vote.variant_id_loser,
      referenceVariantId
    );

    if (winnerIsReference === loserIsReference) return;

    const candidateId = winnerIsReference
      ? vote.variant_id_loser
      : vote.variant_id_winner;
    const candidateName = winnerIsReference
      ? vote.loser_variant_name
      : vote.winner_variant_name;

    if (candidateId === null || candidateId === undefined) return;

    if (!candidates.has(String(candidateId))) {
      candidates.set(String(candidateId), {
        candidateId,
        candidateName: candidateName || String(candidateId),
        referenceWins: 0,
        candidateWins: 0,
        ties: 0,
        equallyBad: 0,
      });
    }

    const result = candidates.get(String(candidateId));
    const voteType = vote.vote_type || "winner_loser";

    if (voteType === "tie") {
      result.ties += 1;
    } else if (voteType === "equally_bad") {
      result.equallyBad += 1;
    } else if (winnerIsReference) {
      result.referenceWins += 1;
    } else {
      result.candidateWins += 1;
    }
  });

  return [...candidates.values()]
    .map((result) => {
      const outcomeCount =
        result.referenceWins + result.candidateWins + result.ties;
      return {
        ...result,
        outcomeCount,
        totalCount: outcomeCount + result.equallyBad,
        referenceWinShare:
          outcomeCount > 0 ? result.referenceWins / outcomeCount : 0,
        candidateWinPercentage:
          outcomeCount > 0 ? (result.candidateWins / outcomeCount) * 100 : 0,
        tiePercentage:
          outcomeCount > 0 ? (result.ties / outcomeCount) * 100 : 0,
        referenceWinPercentage:
          outcomeCount > 0 ? (result.referenceWins / outcomeCount) * 100 : 0,
      };
    })
    .sort(
      (left, right) =>
        right.referenceWinShare - left.referenceWinShare ||
        left.candidateName.localeCompare(right.candidateName)
    );
};

export { summarizeReferenceResults };
